<template>
  <section
    class="tw:flex tw:flex-col tw:overflow-y-auto"
    :class="{ 'tw:hidden': hideInspector }">
    <div
      id="object-style-inspector"
      class="tw:min-h-0 tw:overflow-x-hidden tw:shadow-md">
      <AccordionRoot
        type="multiple"
        :default-value="['appearance']"
        class="tw:flex tw:flex-col tw:gap-1">
        <!-- General Appearance Section -->
        <AccordionItem
          value="appearance"
          class="accordion-item">
          <AccordionHeader>
            <AccordionTrigger class="accordion-trigger tw:group">
              <div class="tw:flex tw:items-center tw:gap-2">
                <ChevronRightIcon
                  class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:opacity-60 tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-90" />
                <span class="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase">General</span>
              </div>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent class="accordion-content">
            <div class="tw:flex tw:flex-col tw:gap-1.5 tw:pt-0.5 tw:pb-1.5 tw:pl-5">
              <!-- Background -->
              <div :class="!isPropertyEnabled('fill') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Background</div>
                <div class="tw:flex tw:items-center tw:justify-between tw:py-0.5">
                  <div class="tw:-ml-px tw:flex tw:justify-between tw:gap-0.5">
                    <ColorSwatch
                      v-for="(color, index) in colorPalette.filter((_, index) => index <= 4)"
                      :selected="color === config.fill"
                      small
                      :color="color"
                      v-model="config.fill"
                      :key="`background-${color}-${index}`" />
                  </div>
                  <div class="tw:mx-2 tw:my-0 tw:h-7 tw:w-px tw:bg-slate-500"></div>
                  <ColorPickerMenu
                    name="backgroundCustomColorMenuButton"
                    :color="config.fill"
                    :colors="colorPalette.filter((_, index) => index <= 4)"
                    v-model="config.fill" />
                </div>
              </div>

              <!-- Text Color -->
              <div :class="!isPropertyEnabled('textColor') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Text Color</div>
                <div class="tw:my-1 tw:flex tw:items-center tw:justify-start tw:py-0.5">
                  <ColorPickerMenu
                    name="textCustomColorMenuButton"
                    :color="config.textColor"
                    :colors="[]"
                    v-model="config.textColor" />
                </div>
              </div>

              <!-- Opacity -->
              <div
                :class="!isPropertyEnabled('opacity') && 'disabled-option'"
                class="tw:relative tw:pb-5">
                <div class="tw:text-xs tw:opacity-70">Opacity</div>
                <input
                  v-model="opacity"
                  type="range"
                  step="10"
                  min="10"
                  max="100"
                  class="tw:range tw:range-primary tw:opacity-70 tw:range-xs" />
                <div class="tw:absolute tw:top-9 tw:flex tw:w-full tw:justify-between tw:px-1 tw:text-xs">
                  <div
                    v-for="i in 10"
                    :key="i"
                    class="tw:flex tw:flex-col tw:items-center">
                    <div class="tw:scale-y-50 tw:opacity-40">|</div>
                    <div
                      class="tw:absolute tw:top-2.5 tw:text-[10px]"
                      v-show="opacity === i * 10">
                      {{ i * 10 }}%
                    </div>
                  </div>
                </div>
              </div>

              <!-- Size Mode -->
              <div
                v-if="isPropertyEnabled('width') || isPropertyEnabled('height')"
                :class="!isPropertyEnabled('width') && !isPropertyEnabled('height') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Mode</div>
                <div class="tw:flex tw:items-center tw:justify-start tw:py-0.5">
                  <div class="tw:flex tw:gap-2">
                    <label
                      for="size-mode-pixel"
                      class="tw:label tw:cursor-pointer tw:rounded tw:px-2 tw:py-0">
                      <span class="tw:label-text tw:text-xs">Pixel</span>
                      <input
                        id="size-mode-pixel"
                        type="radio"
                        name="size-mode"
                        value="pixel"
                        v-model="sizeMode"
                        class="tw:radio tw:radio-xs tw:opacity-100" />
                    </label>
                    <label
                      for="size-mode-grid"
                      class="tw:label tw:cursor-pointer tw:rounded tw:px-2 tw:py-0">
                      <span class="tw:label-text tw:text-xs">Grid</span>
                      <input
                        id="size-mode-grid"
                        type="radio"
                        name="size-mode"
                        value="grid"
                        v-model="sizeMode"
                        class="tw:radio tw:radio-xs tw:opacity-100" />
                    </label>
                  </div>
                </div>
              </div>

              <!-- Width -->
              <div
                v-if="isPropertyEnabled('width')"
                :class="!isPropertyEnabled('width') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Width</div>
                <div class="tw:flex tw:items-center tw:justify-start tw:py-0.5">
                  <div class="tw:relative tw:w-full">
                    <input
                      v-model.number="displayWidth"
                      type="number"
                      min="1"
                      :step="sizeMode === 'grid' ? 1 : 1"
                      class="size-input tw:input-bordered tw:input tw:input-sm tw:w-full" />
                    <span class="size-input-unit">
                      {{ sizeMode === 'grid' ? 'squares' : 'px' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Height -->
              <div
                v-if="isPropertyEnabled('height')"
                :class="!isPropertyEnabled('height') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Height</div>
                <div class="tw:flex tw:items-center tw:justify-start tw:py-0.5">
                  <div class="tw:relative tw:w-full">
                    <input
                      v-model.number="displayHeight"
                      type="number"
                      min="1"
                      :step="sizeMode === 'grid' ? 1 : 1"
                      class="size-input tw:input-bordered tw:input tw:input-sm tw:w-full" />
                    <span class="size-input-unit">
                      {{ sizeMode === 'grid' ? 'squares' : 'px' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <!-- Border Section -->
        <AccordionItem
          value="border"
          class="accordion-item">
          <AccordionHeader>
            <AccordionTrigger class="accordion-trigger tw:group">
              <div class="tw:flex tw:items-center tw:gap-2">
                <ChevronRightIcon
                  class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:opacity-60 tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-90" />
                <span class="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase">Border</span>
              </div>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent class="accordion-content">
            <div class="tw:flex tw:flex-col tw:gap-1.5 tw:pt-0.5 tw:pb-1.5 tw:pl-5">
              <!-- Border Color -->
              <div :class="!isPropertyEnabled('stroke') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Color</div>
                <div class="tw:flex tw:items-center tw:justify-between tw:py-0.5">
                  <div class="tw:-ml-px tw:flex tw:justify-between tw:gap-0.5">
                    <ColorSwatch
                      v-for="(color, index) in colorPalette.filter((_, index) => index <= 4)"
                      :selected="color === config.stroke"
                      small
                      :color="color"
                      v-model="config.stroke"
                      :key="`border-${color}-${index}`" />
                  </div>
                  <div class="tw:mx-2 tw:my-0 tw:h-7 tw:w-px tw:bg-slate-500"></div>
                  <ColorPickerMenu
                    name="borderCustomColorMenuButton"
                    :color="config.stroke"
                    :colors="colorPalette.filter((_, index) => index <= 4)"
                    v-model="config.stroke" />
                </div>
              </div>

              <!-- Border Width -->
              <div :class="!isPropertyEnabled('stroke') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Width</div>
                <div class="radio-list tw:py-0.5">
                  <label
                    class="tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="config.strokeWidth === 0 ? 'selected' : 'not-selected'">
                    <input
                      type="radio"
                      title="None"
                      class="tw:hidden"
                      :value="0"
                      v-model="config.strokeWidth" />
                    <svg
                      viewBox="-3 -3 32 32"
                      aria-hidden="true"
                      focusable="false"
                      role="img">
                      <path
                        fill="currentColor"
                        d="M1,4.27L2.28,3L21,21.72L19.73,23L17,20.27V21H15V19H15.73L5,8.27V9H3V7H3.73L1,4.27M20,3A1,1 0 0,1 21,4V5H19V3H20M15,5V3H17V5H15M11,5V3H13V5H11M7,5V3H9V5H7M11,21V19H13V21H11M7,21V19H9V21H7M4,21A1,1 0 0,1 3,20V19H5V21H4M3,15H5V17H3V15M21,15V17H19V15H21M3,11H5V13H3V11M21,11V13H19V11H21M21,7V9H19V7H21Z" />
                    </svg>
                  </label>
                  <label
                    class="tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="config.strokeWidth === 3 ? 'selected' : 'not-selected'">
                    <input
                      type="radio"
                      title="Thin"
                      class="tw:hidden"
                      :value="3"
                      v-model="config.strokeWidth" />
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      role="img"
                      viewBox="0 0 20 20"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round">
                      <path
                        d="M4.167 10h11.666"
                        stroke="currentColor"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </label>
                  <label
                    class="tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="config.strokeWidth === 5 ? 'selected' : 'not-selected'">
                    <input
                      type="radio"
                      title="Bold"
                      class="tw:hidden"
                      :value="5"
                      v-model="config.strokeWidth" />
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      role="img"
                      viewBox="0 0 20 20"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round">
                      <path
                        d="M5 10h10"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </label>
                  <label
                    class="tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="config.strokeWidth === 10 ? 'selected' : 'not-selected'">
                    <input
                      type="radio"
                      title="Extra Bold"
                      class="tw:hidden"
                      :value="10"
                      v-model="config.strokeWidth" />
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      role="img"
                      viewBox="0 0 20 20"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round">
                      <path
                        d="M5 10h10"
                        stroke="currentColor"
                        stroke-width="3.75"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </label>
                </div>
              </div>

              <!-- Border Style -->
              <div
                v-if="config.strokeWidth !== 0"
                :class="!isPropertyEnabled('stroke') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Style</div>
                <div class="radio-list tw:py-0.5">
                  <label
                    class="tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="strokeStyle === 'solid' ? 'selected' : 'not-selected'">
                    <input
                      type="radio"
                      title="Solid"
                      class="tw:hidden"
                      value="solid"
                      v-model="strokeStyle" />
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      role="img"
                      viewBox="0 0 20 20"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round">
                      <path
                        d="M4.167 10h11.666"
                        stroke="currentColor"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </label>
                  <label
                    class="tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="strokeStyle === 'dashed' ? 'selected' : 'not-selected'">
                    <input
                      type="radio"
                      title="Dashed"
                      class="tw:hidden"
                      value="dashed"
                      v-model="strokeStyle" />
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      role="img"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round">
                      <g stroke-width="2">
                        <path
                          stroke="none"
                          d="M0 0h24v24H0z"
                          fill="none" />
                        <path d="M5 12h2" />
                        <path d="M17 12h2" />
                        <path d="M11 12h2" />
                      </g>
                    </svg>
                  </label>
                  <label
                    class="tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="strokeStyle === 'dotted' ? 'selected' : 'not-selected'">
                    <input
                      type="radio"
                      title="Dotted"
                      class="tw:hidden"
                      value="dotted"
                      v-model="strokeStyle" />
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      role="img"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round">
                      <g stroke-width="2">
                        <path
                          stroke="none"
                          d="M0 0h24v24H0z"
                          fill="none" />
                        <path d="M4 12v.01" />
                        <path d="M8 12v.01" />
                        <path d="M12 12v.01" />
                        <path d="M16 12v.01" />
                        <path d="M20 12v.01" />
                      </g>
                    </svg>
                  </label>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <!-- Coverage Type Section (conditional) -->
        <AccordionItem
          v-if="config.coverageTypeEnabled"
          value="coverage"
          class="accordion-item">
          <AccordionHeader>
            <AccordionTrigger class="accordion-trigger tw:group">
              <div class="tw:flex tw:items-center tw:gap-2">
                <ChevronRightIcon
                  class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:opacity-60 tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-90" />
                <span class="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase">Coverage Type</span>
              </div>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent class="accordion-content">
            <div class="tw:flex tw:flex-col tw:gap-1.5 tw:pt-0.5 tw:pb-1.5 tw:pl-5">
              <div>
                <div class="tw:text-xs tw:opacity-70">Font Color</div>
                <div class="tw:flex tw:items-center tw:justify-between tw:py-0.5">
                  <div class="tw:-ml-px tw:flex tw:justify-between tw:gap-0.5">
                    <ColorSwatch
                      v-for="(color, index) in colorPalette.filter((_, index) => index <= 4)"
                      :selected="color === config.coverageContainerStroke"
                      small
                      :color="color"
                      v-model="config.coverageContainerStroke"
                      :key="`coverage-stroke-${color}-${index}`" />
                  </div>
                  <div class="tw:mx-2 tw:my-0 tw:h-7 tw:w-px tw:bg-slate-500"></div>
                  <ColorPickerMenu
                    name="coverageStrokeColorMenuButton"
                    :color="config.coverageContainerStroke"
                    :colors="colorPalette.filter((_, index) => index >= 5)"
                    v-model="config.coverageContainerStroke" />
                </div>
              </div>

              <div>
                <div class="tw:text-xs tw:opacity-70">Background</div>
                <div class="tw:flex tw:items-center tw:justify-between tw:py-0.5">
                  <div class="tw:-ml-px tw:flex tw:justify-between tw:gap-0.5">
                    <ColorSwatch
                      v-for="(color, index) in colorPalette.filter((_, index) => index <= 4)"
                      :selected="color === config.coverageContainerFill"
                      small
                      :color="color"
                      v-model="config.coverageContainerFill"
                      :key="`coverage-fill-${color}-${index}`" />
                  </div>
                  <div class="tw:mx-2 tw:my-0 tw:h-7 tw:w-px tw:bg-slate-500"></div>
                  <ColorPickerMenu
                    name="coverageFillColorMenuButton"
                    :color="config.coverageContainerFill"
                    :colors="colorPalette.filter((_, index) => index >= 5)"
                    v-model="config.coverageContainerFill" />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <!-- Typography Section -->
        <AccordionItem
          value="typography"
          class="accordion-item">
          <AccordionHeader>
            <AccordionTrigger class="accordion-trigger tw:group">
              <div class="tw:flex tw:items-center tw:gap-2">
                <ChevronRightIcon
                  class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:opacity-60 tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-90" />
                <span class="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase">Typography</span>
              </div>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent class="accordion-content">
            <div class="tw:flex tw:flex-col tw:gap-1.5 tw:pt-0.5 tw:pb-1.5 tw:pl-5">
              <!-- Font Family -->
              <div :class="!isPropertyEnabled('textStyle') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Font Family</div>
                <div
                  class="radio-list tw:py-0.5"
                  style="width: 100%; margin: 0 auto">
                  <label
                    class="font-selection tw:flex tw:h-8 tw:w-8 tw:font-sans tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="textStyle === 'sans-serif' && 'selected'">
                    <input
                      type="radio"
                      title="Sans Serif"
                      class="tw:hidden"
                      value="sans-serif"
                      v-model="textStyle" />
                    <div class="font-button">A</div>
                  </label>
                  <label
                    class="font-selection font-serif tw:flex tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="textStyle === 'serif' && 'selected'">
                    <input
                      type="radio"
                      title="Serif"
                      class="tw:hidden"
                      value="serif"
                      v-model="textStyle" />
                    <div class="font-button">A</div>
                  </label>
                  <label
                    class="font-selection font-monospace tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="textStyle === 'monospace' && 'selected'">
                    <input
                      type="radio"
                      title="Monospace"
                      class="tw:hidden"
                      value="monospace"
                      v-model="textStyle" />
                    <div class="font-button">A</div>
                  </label>
                </div>
              </div>

              <!-- Font Size -->
              <div :class="!isPropertyEnabled('fontSize') && 'disabled-option'">
                <div class="tw:text-xs tw:opacity-70">Font Size</div>
                <div
                  class="radio-list tw:py-0.5"
                  style="width: 100%; margin: 0">
                  <label
                    title="Extra Small"
                    class="font-size tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="fontSize === fontSizes[0] && 'selected'">
                    <input
                      type="radio"
                      class="tw:hidden"
                      :value="fontSizes[0]"
                      v-model="fontSize" />
                    <div class="font-button"><div>XS</div></div>
                  </label>
                  <label
                    title="Small"
                    class="font-size tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="fontSize === fontSizes[1] && 'selected'">
                    <input
                      type="radio"
                      class="tw:hidden"
                      :value="fontSizes[1]"
                      v-model="fontSize" />
                    <div class="font-button"><div>S</div></div>
                  </label>
                  <label
                    title="Medium"
                    class="font-size tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="fontSize === fontSizes[2] && 'selected'">
                    <input
                      type="radio"
                      class="tw:hidden"
                      :value="fontSizes[2]"
                      v-model="fontSize" />
                    <div class="font-button"><div>M</div></div>
                  </label>
                  <label
                    title="Large"
                    class="font-size tw:h-8 tw:w-8 tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10"
                    :class="fontSize === fontSizes[3] && 'selected'">
                    <input
                      type="radio"
                      class="tw:hidden"
                      :value="fontSizes[3]"
                      v-model="fontSize" />
                    <div class="font-button"><div>L</div></div>
                  </label>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <!-- Layout & Stacking Section -->
        <AccordionItem
          value="layout"
          class="accordion-item">
          <AccordionHeader>
            <AccordionTrigger class="accordion-trigger tw:group">
              <div class="tw:flex tw:items-center tw:gap-2">
                <ChevronRightIcon
                  class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:opacity-60 tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-90" />
                <span class="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase">Layout & Stacking</span>
              </div>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent class="accordion-content">
            <div class="tw:flex tw:flex-col tw:gap-1.5 tw:pt-0.5 tw:pb-1.5 tw:pl-5">
              <!-- Layers -->
              <div>
                <div class="tw:text-xs tw:opacity-70">Layers</div>
                <div class="tw:flex tw:justify-start tw:gap-2 tw:py-0.5">
                  <div
                    title="Send to back"
                    @click="updateLayerOrder('send-to-back')"
                    class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-lg tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                    <img
                      :src="layer_send_to_back"
                      alt="Send to back"
                      class="tw:h-4 tw:w-4" />
                  </div>
                  <div
                    title="Send backward"
                    @click="updateLayerOrder('send-backward')"
                    class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-lg tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                    <img
                      :src="layer_send_backward"
                      alt="Send backward"
                      class="tw:h-4 tw:w-4" />
                  </div>
                  <div
                    title="Bring to front"
                    @click="updateLayerOrder('bring-to-front')"
                    class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-lg tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                    <img
                      :src="layer_bring_to_front"
                      alt="Bring to front"
                      class="tw:h-4 tw:w-4" />
                  </div>
                  <div
                    title="Bring forward"
                    @click="updateLayerOrder('bring-forward')"
                    class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-lg tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                    <img
                      :src="layer_bring_forward"
                      alt="Bring forward"
                      class="tw:h-4 tw:w-4" />
                  </div>
                </div>
              </div>

              <!-- Scale Mode -->
              <div
                v-if="isPropertyEnabled('scaleMode')"
                class="tw:flex tw:flex-col">
                <div class="tw:text-xs tw:opacity-70">Scale Mode</div>
                <div class="tw:join-vertical tw:join tw:w-1/2">
                  <label
                    for="scale-mode-dynamic"
                    class="tw:label tw:join-item tw:cursor-pointer">
                    <span class="tw:label-text tw:text-xs">Dynamic</span>
                    <input
                      id="scale-mode-dynamic"
                      type="radio"
                      name="scale-mode"
                      value="dynamic"
                      v-model="towerScaleMode"
                      class="tw:radio tw:radio-xs tw:opacity-100"
                      :class="{ 'not-selected': true }" />
                  </label>
                  <label
                    for="scale-mode-aggregate"
                    class="tw:label tw:join-item tw:cursor-pointer">
                    <span class="tw:label-text tw:text-xs">Aggregate</span>
                    <input
                      id="scale-mode-aggregate"
                      type="radio"
                      name="scale-mode"
                      value="aggregate"
                      v-model="towerScaleMode"
                      class="tw:radio tw:radio-xs tw:opacity-100"
                      :class="{ 'not-selected': true }" />
                  </label>
                  <label
                    for="scale-mode-occurrence"
                    class="tw:label tw:join-item tw:cursor-pointer">
                    <span class="tw:label-text tw:text-xs">Occurrence</span>
                    <input
                      id="scale-mode-occurrence"
                      type="radio"
                      name="scale-mode"
                      value="occurrence"
                      v-model="towerScaleMode"
                      class="tw:radio tw:radio-xs tw:opacity-100"
                      :class="{ 'not-selected': true }" />
                  </label>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <!-- Actions Section -->
        <AccordionItem
          value="actions"
          class="accordion-item">
          <AccordionHeader>
            <AccordionTrigger class="accordion-trigger tw:group">
              <div class="tw:flex tw:items-center tw:gap-2">
                <ChevronRightIcon
                  class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:opacity-60 tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-90" />
                <span class="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase">Actions</span>
              </div>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent class="accordion-content">
            <div class="tw:flex tw:flex-col tw:gap-1.5 tw:pt-0.5 tw:pb-1.5 tw:pl-5">
              <div class="tw:flex tw:flex-wrap tw:justify-start tw:gap-2">
                <div
                  :title="isPolicyLine ? 'Edit Policy Line' : 'Edit Policy'"
                  v-if="isPropertyEnabled('editPolicy') && !isGroupSelected()"
                  @click="editPolicy()"
                  class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:rounded-lg tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                  <SquarePenIcon class="tw:mx-auto tw:my-auto tw:max-h-5 tw:max-w-5 tw:rounded-none tw:fill-none" />
                </div>
                <div
                  title="Duplicate"
                  :class="!isPropertyEnabled('duplicate') && 'disabled-option'"
                  @click="duplicateObject()"
                  class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:rounded-lg tw:p-[5px] tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                  <Copy class="tw:mx-auto tw:my-auto tw:max-h-5 tw:max-w-5 tw:rounded-none tw:fill-none" />
                </div>
                <div
                  title="Delete"
                  :class="!isPropertyEnabled('delete') && 'disabled-option'"
                  @click="deleteObject()"
                  class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:rounded-lg tw:p-[5px] tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                  <Trash2 class="tw:mx-auto tw:my-auto tw:max-h-5 tw:max-w-5 tw:rounded-none tw:fill-none" />
                </div>
                <div
                  title="Toggle Retention"
                  :class="(!isPropertyEnabled('toggleRetention') || !isPrimaryPolicyRect) && 'disabled-option'"
                  @click="toggleRetention()"
                  class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:rounded-lg tw:p-[5px] tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                  <PanelBottom class="tw:mx-auto tw:my-auto tw:max-h-5 tw:max-w-5 tw:rounded-none tw:fill-none" />
                </div>
                <div
                  :title="labelFormat === LabelFormat.Default ? 'Inline Label' : 'Stacked Label'"
                  :class="!isPropertyEnabled('toggleLabelFormat') && 'disabled-option'"
                  @click="toggleLabelFormat()"
                  class="action-button tw:flex tw:h-8 tw:w-8 tw:cursor-pointer tw:rounded-lg tw:p-[5px] tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
                  <Minus
                    v-if="labelFormat === LabelFormat.Default"
                    class="tw:mx-auto tw:my-auto tw:h-full tw:w-full tw:fill-none" />
                  <MenuIcon
                    v-else
                    class="tw:mx-auto tw:my-auto tw:h-full tw:w-full tw:fill-none" />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    </div>
  </section>
</template>

<script lang="ts">
import { StoreMutationType } from '@/types/StoreMutationType';
import { fontFamily, FONT_SIZES, strokeDimensions, TowerObjectConfig } from '@/types/tower/Tower';
import { MutationPayload, useStore } from 'vuex';
import { AppState, Commit, Store } from '@/store';
import ColorPickerMenu from './ColorPickerMenu.vue';
import ColorSwatch from './ColorSwatch.vue';
import { TowerScaleMode } from '@/views/tower/v2/objects/tower_scale/TowerScale';
import { CHANGE_OBJECT_LAYER, ChangeObjectLayerOptions, TOWER_SCALE_MODE_CHANGED } from '@/store/CustomEvents';
import layer_send_to_back from '@/assets/images/layer_send_to_back.svg';
import layer_send_backward from '@/assets/images/layer_send_backward.svg';
import layer_bring_forward from '@/assets/images/layer_bring_forward.svg';
import layer_bring_to_front from '@/assets/images/layer_bring_to_front.svg';
import { Minus, MenuIcon, SquarePenIcon, Copy, Trash2, PanelBottom, ChevronRightIcon } from 'lucide-vue-next';
import { LabelFormat } from '@/views/tower/v2/objects/policy_rect/PolicyRect';
import { paletteColors } from '@/utilities/Color';
import { AccordionContent, AccordionHeader, AccordionItem, AccordionRoot, AccordionTrigger } from 'reka-ui';

const enabledProperties = new Map<string, Set<string>>([
  ['opacity', new Set(['i-text', 'custom-policy-rect', 'triangle', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['textStyle', new Set(['i-text', 'custom-policy-rect', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['textColor', new Set(['i-text', 'custom-policy-rect', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['fontSize', new Set(['i-text', 'custom-policy-rect', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['fill', new Set(['custom-policy-rect', 'triangle', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['stroke', new Set(['custom-policy-rect', 'triangle', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['strokeWidth', new Set(['custom-policy-rect', 'triangle', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['strokeStyle', new Set(['custom-policy-rect', 'triangle', 'policy-rect', 'labeled-rect', 'legend-rect'])],
  ['width', new Set(['policy-rect', 'custom-policy-rect', 'labeled-rect'])],
  ['height', new Set(['policy-rect', 'custom-policy-rect', 'labeled-rect'])],
  ['duplicate', new Set(['i-text', 'custom-policy-rect', 'labeled-rect', 'triangle'])],
  ['delete', new Set(['i-text', 'custom-policy-rect', 'triangle', 'policy-rect', 'labeled-rect', 'legend-rect', 'tower-scale'])],
  ['editPolicy', new Set(['custom-policy-rect', 'policy-rect'])],
  ['toggleRetention', new Set(['policy-rect', 'custom-policy-rect'])],
  ['toggleLabelFormat', new Set(['policy-rect', 'custom-policy-rect'])],
  ['scaleMode', new Set(['tower-scale'])],
]);

const config = {
  opacity: 1,
  fill: '',
  stroke: '',
  strokeWidth: 5,
  strokeStyle: '',
  strokeDashArray: [0],
  textStyle: '',
  textColor: '',
  fontFamily: '',
  fontSize: 18,
  coverageTypeEnabled: false,
  coverageContainerFill: '',
  coverageContainerStroke: '',
  width: undefined as number | undefined,
  height: undefined as number | undefined,
};

export default {
  name: 'Inspector',
  components: {
    ColorPickerMenu,
    ColorSwatch,
    Minus,
    MenuIcon,
    SquarePenIcon,
    Copy,
    Trash2,
    PanelBottom,
    ChevronRightIcon,
    AccordionContent,
    AccordionHeader,
    AccordionItem,
    AccordionRoot,
    AccordionTrigger,
  },
  setup() {
    const store = useStore<AppState>();
    if (!store) {
      throw new Error('store is undefined');
    }
    return {
      store,
      layer_send_to_back,
      layer_send_backward,
      layer_bring_to_front,
      layer_bring_forward,
    };
  },
  data: () => ({
    config: { ...config },
    selectedObject: undefined as undefined | TowerObjectConfig,
    changed: new Set<string>(),
    original: { ...config },
    fontSizes: FONT_SIZES,
    towerScaleMode: 'dynamic' as TowerScaleMode | undefined,
    sizeMode: 'pixel' as 'pixel' | 'grid',
    initialGridSnap: true as boolean,
  }),
  computed: {
    LabelFormat() {
      return LabelFormat;
    },
    hideInspector() {
      return !this.selectedObject;
    },
    colorPalette() {
      return paletteColors;
    },
    isPrimaryPolicyRect() {
      const activeObject = Store.state.towerCanvasState.canvasObjects.find((o) => o.id === this.selectedObject?.ids[0]);
      if (activeObject instanceof fabric.PolicyRect) {
        return activeObject?.isPrimary ?? false;
      }
      return false;
    },
    isPolicyLine() {
      const activeObject = Store.state.towerCanvasState.canvasObjects.find((o) => o.id === this.selectedObject?.ids[0]);
      if (activeObject instanceof fabric.PolicyRect) {
        return activeObject?.getPolicyData().isChildPolicy ?? true;
      }
      return false;
    },
    labelFormat() {
      const activeObject = Store.state.towerCanvasState.canvasObjects.find((o) => o.id === this.selectedObject?.ids[0]);
      if (activeObject instanceof fabric.PolicyRect) {
        return activeObject?.labelFormat ?? LabelFormat.Default;
      }
      return LabelFormat.Default;
    },
    opacity: {
      get(): number {
        return this.config.opacity * 100;
      },
      set(val: number) {
        if (val > 100) val = 100;
        if (val < 10) val = 10;
        this.config.opacity = val / 100;
      },
    },
    strokeStyle: {
      get(): string {
        return this.config.strokeStyle;
      },
      set(val: string) {
        this.config.strokeStyle = val;
        this.config.strokeDashArray = strokeDimensions(val, this.config.strokeWidth);
        this.updateConfigState();
      },
    },
    textStyle: {
      get(): string {
        return this.config.textStyle;
      },
      set(val: string) {
        this.config.textStyle = val;
        this.config.fontFamily = fontFamily(val);
        this.updateConfigState();
      },
    },
    fontSize: {
      get(): number {
        return this.config.fontSize;
      },
      set(val: number) {
        this.config.fontSize = val;
        this.updateConfigState();
      },
    },
    textColor: {
      get(): string {
        return this.config.textColor;
      },
      set(val: string) {
        this.config.textColor = val;
        this.updateConfigState();
      },
    },
    displayWidth: {
      get(): number | undefined {
        if (this.config.width === undefined) return undefined;
        const gridSize = this.store.state.towerCanvasState.gridSquareSize;

        if (this.sizeMode === 'grid') {
          // Convert pixel value to grid units and round
          return Math.round(this.config.width / gridSize);
        }

        // Pixel mode: return rounded pixel value (no decimals)
        return Math.round(this.config.width);
      },
      set(val: number | undefined) {
        if (val === undefined || val <= 0) return;
        const gridSize = this.store.state.towerCanvasState.gridSquareSize;
        let processedVal: number;

        if (this.sizeMode === 'grid') {
          // Convert grid units to pixels
          processedVal = val * gridSize;

          if (this.initialGridSnap) {
            // First adjustment: snap to nearest grid increment
            processedVal = this.snapToGrid(processedVal, gridSize);
            this.initialGridSnap = false;
          } else {
            // Subsequent adjustments: round to grid increment
            processedVal = this.roundToGrid(processedVal, gridSize);
          }
        } else {
          // Pixel mode: use value directly (already rounded by getter)
          processedVal = val;
        }

        this.config.width = processedVal;
        this.updateConfigState();
      },
    },
    displayHeight: {
      get(): number | undefined {
        if (this.config.height === undefined) return undefined;
        const gridSize = this.store.state.towerCanvasState.gridSquareSize;

        if (this.sizeMode === 'grid') {
          // Convert pixel value to grid units and round
          return Math.round(this.config.height / gridSize);
        }

        // Pixel mode: return rounded pixel value (no decimals)
        return Math.round(this.config.height);
      },
      set(val: number | undefined) {
        if (val === undefined || val <= 0) return;
        const gridSize = this.store.state.towerCanvasState.gridSquareSize;
        let processedVal: number;

        if (this.sizeMode === 'grid') {
          // Convert grid units to pixels
          processedVal = val * gridSize;

          if (this.initialGridSnap) {
            // First adjustment: snap to nearest grid increment
            processedVal = this.snapToGrid(processedVal, gridSize);
            this.initialGridSnap = false;
          } else {
            // Subsequent adjustments: round to grid increment
            processedVal = this.roundToGrid(processedVal, gridSize);
          }
        } else {
          // Pixel mode: use value directly (already rounded by getter)
          processedVal = val;
        }

        this.config.height = processedVal;
        this.updateConfigState();
      },
    },
  },
  methods: {
    updateConfigState() {
      if (!this.selectedObject || !this.selectedObject.types || this.selectedObject.types.length === 0) return;

      // find props updated by the user by comparing against original state
      Object.entries(this.config).forEach((entry) => {
        if (this.original[entry[0] as keyof typeof config] !== entry[1]) {
          this.changed.add(entry[0]);
        }
      });

      // only commit a change if there is a difference
      if (this.changed.size > 0) {
        this.store.commit(StoreMutationType.SET_TOWER_CONFIG_CHANGE, {
          ...(this.selectedObject as TowerObjectConfig),
          ...this.config,
          updatedProps: Array.from(this.changed),
        });

        // reset the original state for when the user makes another change before deselecting an object
        this.changed.clear();
        this.original = { ...this.config };
      }
    },
    /**
     * Some object types have properties that should not be changed (e.g. borders on text objects, etc.). However, when
     * a combination of objects are selected (e.g. text and rect) then the border option should display (and will only
     * apply to the rect). The `selectedObject` is based on the first object in the canvas' active selection which
     * reflects the state of the inspector, but the user can change options and have it apply (where applicable) to
     * all selected objects.
     *
     * @param property
     */
    isPropertyEnabled(property: string): boolean {
      if (!this.selectedObject || !this.selectedObject.types || this.selectedObject.types.length === 0) return false;

      const objectTypes = Array.from(new Set(this.selectedObject.types));
      return objectTypes.reduce((acc: boolean, t: string): boolean => {
        return acc || (enabledProperties.get(property)?.has(t) ?? false);
      }, false);
    },
    duplicateObject() {
      this.store.commit(StoreMutationType.DUPLICATE_FABRIC_OBJECT, true);
    },
    deleteObject() {
      this.store.commit(StoreMutationType.DELETE_FABRIC_OBJECT, true);
    },
    editPolicy() {
      Commit(StoreMutationType.SET_TOWER_EDITOR_EXPANDED, true);
    },
    toggleRetention() {
      const activeObjects = Store.state.towerCanvasState.canvasObjects.filter((o) => this.selectedObject?.ids?.includes(o.id));
      activeObjects.forEach((obj) => {
        if (obj instanceof fabric.PolicyRect) {
          obj?.toggleRetention();
        }
      });
    },
    toggleLabelFormat() {
      const activeObjects = Store.state.towerCanvasState.canvasObjects.filter((o) => this.selectedObject?.ids?.includes(o.id));
      activeObjects.forEach((obj) => {
        if (obj instanceof fabric.PolicyRect) {
          obj?.toggleLabelFormat();
        }
      });
    },
    isGroupSelected(): boolean {
      if (!this.selectedObject || !this.selectedObject.types) return false;

      return this.selectedObject.types.length > 1;
    },
    handleFillColorChange(value: string): void {
      this.config.fill = value;
    },
    handleStrokeColorChange(value: string): void {
      this.config.stroke = value;
    },
    updateLayerOrder(value: ChangeObjectLayerOptions) {
      switch (value) {
        case 'send-backward':
          dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, { detail: 'send-backward' }));
          break;
        case 'send-to-back':
          dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, { detail: 'send-to-back' }));
          break;
        case 'bring-forward':
          dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, { detail: 'bring-forward' }));
          break;
        case 'bring-to-front':
          dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, { detail: 'bring-to-front' }));
          break;
      }
    },
    snapToGrid(value: number, gridSize: number): number {
      return Math.round(value / gridSize) * gridSize;
    },
    roundToGrid(value: number, gridSize: number): number {
      return Math.round(value / gridSize) * gridSize;
    },
  },
  watch: {
    // props that directly translate to props on `fabric.Object` can simply be watched
    // and be committed to the store (e.g. 'opacity', 'fill'), otherwise properties like
    // 'textStyle' requires the `fabric.Object` property to be computed which requires
    // a computed property
    opacity() {
      this.updateConfigState();
    },
    'config.fill'() {
      this.updateConfigState();
    },
    'config.stroke'() {
      this.updateConfigState();
    },
    'config.strokeWidth'() {
      this.updateConfigState();
    },
    'config.textColor'() {
      this.updateConfigState();
    },
    towerScaleMode() {
      dispatchEvent(new CustomEvent(TOWER_SCALE_MODE_CHANGED, { detail: this.towerScaleMode }));
    },
    'config.coverageContainerFill'() {
      this.updateConfigState();
    },
    'config.coverageContainerStroke'() {
      this.updateConfigState();
    },
    sizeMode(newMode: 'pixel' | 'grid') {
      if (newMode === 'grid' && !this.store.state.towerCanvasState.gridModeEnabled) {
        // Enable grid visualization when switching to grid mode
        this.store.commit(StoreMutationType.SET_GRID_MODE, true);
      }
      // Reset initial snap flag when switching modes
      this.initialGridSnap = true;
    },
  },
  mounted() {
    this.store.subscribe((mutation: MutationPayload) => {
      if (mutation.type === StoreMutationType.SET_TOWER_SELECTED_OBJECT) {
        const payload = mutation.payload as TowerObjectConfig;

        if (!payload) {
          this.selectedObject = undefined;
          this.config = { ...config };
          this.original = { ...config };
          this.changed.clear();
          this.initialGridSnap = true;
          return;
        }

        const cfg = {
          fill: payload.fill,
          opacity: payload.opacity,
          stroke: payload.stroke,
          strokeWidth: payload.strokeWidth,
          strokeDashArray: payload.strokeDashArray,
          textStyle: payload.textStyle,
          fontFamily: payload.fontFamily,
          textColor: payload.textColor,
          strokeStyle: payload.strokeStyle,
          fontSize: payload.fontSize,
          coverageTypeEnabled: payload.coverageTypeEnabled ?? false,
          coverageContainerFill: payload.coverageContainerFill ?? '',
          coverageContainerStroke: payload.coverageContainerStroke ?? '',
          width: payload.width,
          height: payload.height,
        };

        this.towerScaleMode = payload.scaleMode;
        this.selectedObject = payload;
        this.original = { ...cfg };
        this.config = cfg;
        this.initialGridSnap = true;

        this.changed.clear();

        // a text object (type `i-text`) can be initialized _with_ textStyle set,
        // but _without_ a fontFamily set... in this case, update the fontFamily
        // ensure the object is in sync
        if (fontFamily(cfg.textStyle) !== cfg.fontFamily) {
          this.config.fontFamily = fontFamily(this.config.textStyle);
          this.original.fontFamily = this.config.fontFamily;
          this.updateConfigState();
        }
      }
    });
  },
};
</script>

<style scoped>
.layer-color-picker {
  background: none;
}

#object-style-inspector {
  padding: 0.75rem;
  margin: 0.5rem;
  box-sizing: border-box;
  border-radius: 0.5rem;
  opacity: 1;
  background-color: var(--tower-v2-alt-bg-color);
  pointer-events: all;
  width: 240px;
  border: 1px solid var(--tower-v2-border-color);
}

/* Accordion Styles */
.accordion-item {
  border-bottom: 1px solid color-mix(in srgb, var(--tower-v2-border-color) 50%, transparent);
}

.accordion-item:last-child {
  border-bottom: none;
}

.accordion-trigger {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: none;
  outline: none;
}

.accordion-trigger:hover {
  opacity: 0.8;
}

.accordion-content {
  overflow: hidden;
}

.accordion-content[data-state='open'] {
  animation: slideDown 200ms ease-out;
}

.accordion-content[data-state='closed'] {
  animation: slideUp 200ms ease-out;
}

@keyframes slideDown {
  from {
    height: 0;
    opacity: 0;
  }
  to {
    height: var(--reka-accordion-content-height);
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-accordion-content-height);
    opacity: 1;
  }
  to {
    height: 0;
    opacity: 0;
  }
}

label {
  display: flex;
  align-items: center;
  border-radius: 0.5rem;
  margin: 1px;
  transition: background-color 0.05s;
}

.radio-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  display: -ms-flexbox;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: flex-start;
  width: 100%;
}

.radio-list label {
  cursor: pointer;
  height: 2.5rem;
  width: 2.5rem;
  border-radius: 0.5rem;
}

.radio-list svg {
  fill: rgb(242, 242, 242);
  margin: 0.2rem;
  border-radius: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
}

input[type='radio'] {
  opacity: 0;
}

.radio-list input[type='radio'] + svg {
  -webkit-transition: all 0.2s;
  transition: all 0.2s;
}

.radio-list input + svg {
  cursor: pointer;
}

.selected {
  background-color: var(--tower-v2-tool-selected-color);
  transition: background-color 0.05s;
}

.not-selected {
  border: 1px solid var(--tower-v2-border-color);
}

label.font-selection {
  border: 1px solid var(--tower-v2-border-color);
  text-align: center;
  font-size: 1.3rem;
}

label.font-size {
  border: 1px solid var(--tower-v2-border-color);
  text-align: center;
  font-size: 1rem;
}

.font-button {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
}

.font-serif {
  font-family: 'Times New Roman', Times, serif;
}

.font-sans-serif {
  font-family: Helvetica, Arial, sans-serif;
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
}

.disabled-option {
  display: none !important;
}

.action-button {
  border: 1px solid var(--tower-v2-border-color);
}

/* Size input with unit label positioned inside, just left of spinner arrows */
.size-input {
  padding-right: 5rem !important; /* Make room for unit label and spinner */
}

.size-input-unit {
  position: absolute;
  right: 2.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  opacity: 0.6;
  pointer-events: none;
  z-index: 1;
}
</style>
